import React, { useState } from 'react';
import { Search, Package, Truck, CheckCircle } from 'lucide-react';
import DynamicPageSections from '../components/site/DynamicPageSections';
import { useSiteStructureSections } from '../hooks/useSiteStructureSections';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const TrackOrder: React.FC = () => {
  const { sections, ready } = useSiteStructureSections('/track-order');
  const showCmsHeader = ready && sections.length > 0;

  const [orderNumber, setOrderNumber] = useState('');
  const [orderStatus, setOrderStatus] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleTrackOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orderNumber.trim()) return;

    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/shipping/track/${encodeURIComponent(orderNumber.trim())}`);
      const data = await res.json();
      if (data.success && data.data) {
        setOrderStatus(data.data);
      } else {
        setError(data.message || 'Order not found');
        setOrderStatus(null);
      }
    } catch {
      setError('Order not found. Please check your order number.');
      setOrderStatus(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    if (status?.includes('deliver')) return <CheckCircle className="w-6 h-6" />;
    if (status?.includes('ship') || status?.includes('transit')) return <Truck className="w-6 h-6" />;
    return <Package className="w-6 h-6" />;
  };

  const getStatusColor = (status: string) => {
    if (status?.includes('deliver')) return 'text-green-600';
    if (status?.includes('ship') || status?.includes('transit')) return 'text-orange-600';
    return 'text-blue-600';
  };

  return (
    <div className="min-h-screen flex flex-col pb-16 md:pb-0 pt-16">
      {showCmsHeader ? <DynamicPageSections sections={sections} /> : null}
      <main className="flex-grow bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            {!showCmsHeader && (
              <div className="text-center mb-8">
                <Package className="w-16 h-16 text-blue-600 mx-auto mb-4" />
                <h1 className="text-4xl font-bold text-gray-800 mb-4">Track Your Order</h1>
                <p className="text-xl text-gray-600">Enter your order number to track your package</p>
              </div>
            )}

            <div className="bg-white p-8 rounded-lg shadow-sm mb-8">
              <form onSubmit={handleTrackOrder}>
                <div className="flex gap-4">
                  <input type="text" value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} placeholder="Order number" className="flex-1 px-4 py-3 border rounded-lg" required />
                  <button type="submit" disabled={loading} className="px-6 py-3 bg-blue-600 text-white rounded-lg flex items-center gap-2">{loading ? 'Loading…' : <><Search className="w-5 h-5" /> Track</>}</button>
                </div>
              </form>
              {error && <p className="mt-4 text-red-600">{error}</p>}
            </div>
            {orderStatus && (
              <div className="bg-white p-8 rounded-lg shadow-sm space-y-4">
                <h2 className="text-2xl font-bold">Order #{orderStatus.orderNumber}</h2>
                <p className="text-gray-700">{(orderStatus.shippingStatus || orderStatus.status || '').replace(/_/g, ' ')}</p>
                {orderStatus.courierName && <p className="text-sm text-gray-600">Courier: {orderStatus.courierName}</p>}
                {orderStatus.awb && <p className="text-sm">AWB: {orderStatus.awb}</p>}
                {orderStatus.trackingUrl && <a href={orderStatus.trackingUrl} target="_blank" rel="noreferrer" className="text-sm text-blue-600">Tracking link</a>}
                {orderStatus.timeline?.length > 0 && (
                  <ol className="space-y-2 border-t pt-4 text-sm">
                    {orderStatus.timeline.map((ev: { label: string; at?: string }, i: number) => (
                      <li key={i}><strong>{ev.label}</strong>{ev.at ? ` — ${new Date(ev.at).toLocaleString('en-IN')}` : ''}</li>
                    ))}
                  </ol>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default TrackOrder;